/* eslint-disable arrow-body-style */

import { LoadingOverlay } from "@src/common/LoadingOverlay";
import { StyledDateInput } from "@src/common/styled-components";
import { APP_ROUTES, CARE_FORMAT, type CareFormat, CLINICIAN_TYPE } from "@src/constants";
import { MODALITY } from "@src/constants/filter-constants";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useCanvasContext } from "@src/context/CanvasContext";
import { useFeatureFlags } from "@src/context/FeatureFlagContext";
import { useTimerContext } from "@src/context/TimerContext";
import { useCanvasPortalScrollFix } from "@src/hooks/useCanvasPortalScrollFix";
import { useExclusiveAction } from "@src/hooks/useExclusiveAction";
import { useNavigateWithUTM } from "@src/hooks/useNavigateWithUTM";
import { useReleaseFrozenAppointmentOnMount } from "@src/hooks/useReleaseFrozenAppointmentOnMount";
import type {
  Provider,
  ProviderDepartment,
  ProviderSlot,
} from "@src/interfaces/provider.interface";
import type { TimezoneInfo } from "@src/interfaces/timezone.interface";
import { getInitialPatientInformationPath } from "@src/routes/PatientInformation/patientInformationNavigation";
import {
  freezeAppointmentSlots,
  getProviderAvailableDates,
  getProviderAvailableSlots,
} from "@src/services";
import ClientLogsService from "@src/services/client-logs.service";
import {
  getMinimumAppointmentMoment,
  getOrdinalSuffix,
  normalizeToApiDate,
} from "@src/utils/date-utils";
import logger from "@src/utils/logger";
import { buildSchedulingMetadata } from "@src/utils/scheduling-metadata";
import { resolveModalityFromSessionType } from "@src/utils/session-type-modality";
import { isTestRecordBlocked, TEST_RECORD_BLOCK_MESSAGE } from "@src/utils/test-record-utils";
import { dayjs } from "@uhg-abyss/web/tools/dayjs";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Card } from "@uhg-abyss/web/ui/Card";
import { Heading } from "@uhg-abyss/web/ui/Heading";
import { IconSymbol } from "@uhg-abyss/web/ui/IconSymbol";
import { LoadingSpinner } from "@uhg-abyss/web/ui/LoadingSpinner";
import { RadioGroup } from "@uhg-abyss/web/ui/RadioGroup";
import { SelectInput } from "@uhg-abyss/web/ui/SelectInput";
import { Text } from "@uhg-abyss/web/ui/Text";
import moment from "moment-timezone";
import { type CSSProperties, Fragment, type ReactNode, useEffect, useState } from "react";

const FormInfo = styled("div", {
  display: "flex",
  flexDirection: "column",
  marginTop: "16px",
  flexGrow: 1,
  variants: {
    isMobile: {
      true: {
        justifyContent: "space-between",
      },
    },
  },
});

const AppointmentDetailsInfo = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  variants: {
    isMobile: {
      true: {
        gap: "24px",
      },
    },
  },
});

const HourSlotsHolder = styled("div", {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(70px, 90px))",
  gap: "16px",
  marginTop: "8px",
  width: "100%",
  variants: {
    isSmallestMobile: {
      true: {
        gap: "8px",
      },
    },
  },
});

const CardContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  height: "100%",
});

const CardHeaderContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const LoadingSlotsContainer = styled("div", {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "100px",
  width: "100%",
});

const ErrorMessageHolder = styled("div", {
  display: "flex",
  gap: "4px",
  visibility: "hidden",
  margin: "16px 0 12px",
  variants: {
    showError: {
      true: {
        visibility: "visible",
      },
    },
  },
});

const ButtonHolder = styled("div", {
  width: "100%",
  marginTop: "20px",
  marginBottom: "16px",
});

const HourSlot = styled(Button, {
  borderRadius: "4px",
  fontSize: "14px",
  // Set a max width and height to ensure uniform size for all buttons instead of using grid's auto sizing
  // Remove if we want buttons to size based on div content
  maxWidth: "90px",
  maxHeight: "40px",
  width: "100%",
  // end of setting uniform size
  padding: "4px 8px !important",
  whiteSpace: "nowrap", // Prevents text from wrapping
  overflow: "hidden", // Ensures no overflow
  textOverflow: "ellipsis", // Adds ellipsis if the text is too long
});

interface ScheduleCardProps {
  isMobile: boolean;
  isTablet: boolean;
  isSmallestMobile: boolean;
  providerDetails: Provider;
}

export const ScheduleCard = ({
  isMobile,
  isTablet,
  isSmallestMobile,
  providerDetails,
}: ScheduleCardProps) => {
  const { data, setData, markRouteAsComplete, clearAppointmentSelection } = useApplicationContext();
  const { FEATURE_SCHEDULING_CONSOLIDATION } = useFeatureFlags();
  const { isInSalesforce: canvasIsInSalesforce } = useCanvasContext();

  useCanvasPortalScrollFix(canvasIsInSalesforce);

  // State management
  const [selectedDepartment, setSelectedDepartment] = useState<ProviderDepartment | null>(
    data.selectedDepartment || null,
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(
    data.selectedDate ? normalizeToApiDate(data.selectedDate) : null,
  );
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<ProviderSlot | null>(null);
  const [availableDates, setAvailableDates] = useState<dayjs.Dayjs[]>([]);
  const [availableSlots, setAvailableSlots] = useState<ProviderSlot[]>([]);
  const [allowStartTimer, setAllowStartTimer] = useState(true);
  const { startTimer, startTimerWithServerTimestamp, stopTimer } = useTimerContext();
  const [hasFetchedDates, setHasFetchedDates] = useState<boolean>(false);
  // Gates the initial availability fetch until any existing frozen appointment has been
  // released, so a just-freed slot (e.g. the only slot for a day) is included in the
  // first fetch instead of requiring a location switch to refresh.
  const [isUnfreezeComplete, setIsUnfreezeComplete] = useState<boolean>(false);

  // Loading states
  const [isLoadingDates, setIsLoadingDates] = useState<boolean>(false);
  const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(false);
  const [isFreezingAppointment, setIsFreezingAppointment] = useState<boolean>(false);

  // Error states
  const [showError, setShowError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>(
    "That time is no longer available—please choose a different slot.",
  );

  const navigate = useNavigateWithUTM();
  const {
    runExclusive,
    isPending: isBookingPending,
    isUiBlocked,
  } = useExclusiveAction("provider-profile:book-appointment");
  const isSchedulingConsolidationEnabled = Boolean(FEATURE_SCHEDULING_CONSOLIDATION);
  const modalityPreference = resolveModalityFromSessionType(data.sessionType);

  // Get user UUID from context
  const userUuid = data.userUuid;
  const hasFrozenAppointment = Boolean(data.appointmentDetails?.appointmentIds?.length);
  const schedulingMetadata = buildSchedulingMetadata({
    ageRange: data.ageRange,
    clinicianType: providerDetails.clinicianType,
  });

  useEffect(() => {
    setData("selectedProvider", {
      ...providerDetails,
      sessionDuration: schedulingMetadata.sessionDurationMinutes,
    });
  }, [setData, schedulingMetadata.sessionDurationMinutes]);

  const isPrescriber = providerDetails.clinicianType === CLINICIAN_TYPE.PRESCRIBER;

  useReleaseFrozenAppointmentOnMount({
    userUuid,
    hasFrozenAppointment,
    logContext: "ScheduleCard.unfreezeExistingAppointment",
    onReleased: () => {
      clearAppointmentSelection();
      setSelectedTimeSlot(null);
      stopTimer();
    },
    onSettled: () => {
      // Batch with isLoadingDates so there is no commit where unfreeze has
      // completed but the dates effect (a passive effect that runs after this
      // commit) hasn't yet flipped isLoadingDates - that gap would otherwise let
      // the date input become briefly, incorrectly enabled with stale/empty data.
      setIsLoadingDates(true);
      setIsUnfreezeComplete(true);
    },
  });

  const getTimezoneAbbreviation = (): string => {
    // Retrieve timezone from application context
    const timezone: TimezoneInfo | null = data.userTimezone || null;
    return timezone ? timezone.name : "patient";
  };

  /**
   * Check if an error is a timeout error
   */
  const isTimeoutError = (error: unknown): boolean => {
    const errorMessage =
      typeof error === "string" ? error : error instanceof Error ? error.message : "";
    const lowerError = errorMessage.toLowerCase();
    return lowerError.includes("upstream server is timing out") || lowerError.includes("timeout");
  };

  /**
   * Generate department options including virtual option
   */
  const generateDepartmentOptions = (details: Provider) => {
    const modalities = details.modality.map((mod) => mod.toLowerCase());
    const onlyVirtual = modalities.every((mod) => {
      return mod.includes("virtual") || mod.includes(CARE_FORMAT.TELEHEALTH);
    });
    const virtualDept = {
      label: "Virtual session",
      value: "virtual",
      department: {
        department_id: -1,
        clinic_name: "Virtual",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state: "",
        zip_code: "",
        department_latitude: 0,
        department_longitude: 0,
      },
    };
    if (onlyVirtual) {
      // If provider is only virtual, return single virtual option
      return [virtualDept];
    }
    // Otherwise, show department addresses and add virtual if available
    const options = details.departments.map((dept) => {
      return {
        label: `${dept.clinic_name}, ${dept.address_line_1}, ${dept.city}, ${dept.state}`,
        value: dept.department_id?.toString() || dept.clinic_name,
        department: dept,
      };
    });
    // Add virtual option if provider has telehealth
    const hasVirtual = modalities.some(
      (mod) => mod.includes("virtual") || mod.includes(CARE_FORMAT.TELEHEALTH),
    );
    if (hasVirtual) {
      options.push(virtualDept);
    }
    return options;
  };

  const getPhysicalDepartmentIds = (details: Provider): number[] => {
    return details.departments
      .filter((department) => {
        return (
          department.clinic_name !== "Virtual" &&
          Number.isInteger(department.department_id) &&
          department.department_id > 0
        );
      })
      .map((department) => department.department_id);
  };

  const resolveDepartmentIds = (department: ProviderDepartment): number[] => {
    return department.clinic_name === "Virtual"
      ? getPhysicalDepartmentIds(providerDetails) // All physical departments for virtual
      : [department.department_id].filter((departmentId): departmentId is number => {
          return Number.isInteger(departmentId) && departmentId > 0;
        });
  };

  const getMergedAvailabilityError = (
    responses: Array<{ success: boolean; error?: string }>,
  ): string => {
    return (
      responses
        .filter((response) => !response.success && response.error)
        .map((response) => response.error)
        .join("; ") || "Failed to load availability"
    );
  };

  const getCareFormatForDepartment = (department: ProviderDepartment): CareFormat => {
    return department.clinic_name === "Virtual" ? CARE_FORMAT.TELEHEALTH : CARE_FORMAT.IN_PERSON;
  };

  /**
   * Combined (virtual + in-person) availability is only meaningful when the modality is still
   * ambiguous. Availability is never fetched before a Session Location is selected, and every
   * location maps to exactly one care format, so a single request is always sufficient.
   */
  const shouldFetchCombinedAvailability = (department: ProviderDepartment | null): boolean => {
    return (
      isSchedulingConsolidationEnabled && modalityPreference === "no preference" && !department
    );
  };

  const handleCombinedAvailabilityError = (
    errorContext: "dates" | "slots",
    mergedError: string,
  ): void => {
    if (errorContext === "dates") {
      ClientLogsService.logError(
        data.userUuid,
        "ScheduleCard.fetchAvailableDates",
        `Error fetching combined-modality dates for providerId ${Number.parseInt(providerDetails.providerId, 10)}`,
        new Error(mergedError),
      );
    } else {
      ClientLogsService.logError(
        data.userUuid,
        "ScheduleCard.fetchAvailableSlots",
        `Error fetching combined-modality slots for providerId ${Number.parseInt(providerDetails.providerId, 10)}`,
        new Error(mergedError),
      );
    }
  };

  const handleSingleAvailabilityError = (
    errorContext: "dates" | "slots",
    responseError: string,
    careFormat: CareFormat,
  ): void => {
    if (errorContext === "dates") {
      ClientLogsService.logError(
        data.userUuid,
        "ScheduleCard.fetchAvailableDates",
        "Error fetching provider available dates for providerId " +
          parseInt(providerDetails.providerId, 10),
        new Error(responseError),
      );
    } else {
      ClientLogsService.logError(
        data.userUuid,
        "ScheduleCard.fetchAvailableSlots",
        `Error fetching provider available slots for providerId ${Number.parseInt(providerDetails.providerId, 10)}, careFormat ${careFormat}`,
        new Error(responseError),
      );
    }
  };

  const handleAvailabilityFailure = (
    errorContext: "dates" | "slots",
    errorMessage: string,
    shouldResetDatesOrSlots: (() => void) | undefined = undefined,
  ): void => {
    if (isTimeoutError(errorMessage)) {
      navigate(APP_ROUTES.ERROR);
      return;
    }

    if (shouldResetDatesOrSlots) {
      shouldResetDatesOrSlots();
    }

    if (errorContext === "dates") {
      setErrorMessage(errorMessage);
      setShowError(true);
      setAvailableDates([]);
      return;
    }

    setErrorMessage(errorMessage);
    setShowError(true);
    setAvailableSlots([]);
  };

  const setCombinedAvailabilityDates = (
    responses: Array<{
      success: boolean;
      data?: { availableDates: string[] };
      error?: string;
    }>,
  ): void => {
    const successfulResponses = responses.filter(
      (response): response is { success: true; data: { availableDates: string[] } } => {
        return response.success && Boolean(response.data);
      },
    );

    if (successfulResponses.length === 0) {
      const mergedError = getMergedAvailabilityError(responses);
      handleCombinedAvailabilityError("dates", mergedError);
      handleAvailabilityFailure("dates", mergedError, () => setAvailableDates([]));
      return;
    }

    if (successfulResponses.length < responses.length) {
      const mergedError = getMergedAvailabilityError(responses);
      handleCombinedAvailabilityError("dates", mergedError);
    }

    const mergedDates = successfulResponses
      .flatMap((response) => response.data.availableDates ?? [])
      .filter(Boolean);
    const uniqueDates = Array.from(new Set(mergedDates)).sort((firstDate, secondDate) =>
      firstDate.localeCompare(secondDate),
    );
    setAvailableDates(uniqueDates.map((dateStr) => dayjs(dateStr)));
  };

  const setCombinedAvailabilitySlots = (
    responses: Array<{
      response: { success: boolean; data?: { slots: ProviderSlot[] }; error?: string };
      careFormat: CareFormat;
    }>,
  ): void => {
    const successfulResponses = responses.filter(
      (
        entry,
      ): entry is {
        response: { success: true; data: { slots: ProviderSlot[] } };
        careFormat: CareFormat;
      } => {
        return entry.response.success && Boolean(entry.response.data);
      },
    );

    if (successfulResponses.length === 0) {
      const mergedError = getMergedAvailabilityError(responses.map((entry) => entry.response));
      handleCombinedAvailabilityError("slots", mergedError);
      handleAvailabilityFailure("slots", mergedError, () => setAvailableSlots([]));
      return;
    }

    if (successfulResponses.length < responses.length) {
      const mergedError = getMergedAvailabilityError(responses.map((entry) => entry.response));
      handleCombinedAvailabilityError("slots", mergedError);
    }

    const mergedSlots = successfulResponses.flatMap(({ response, careFormat }) => {
      return (response.data.slots ?? []).map((slot) => ({ ...slot, careFormat }));
    });
    mergedSlots.sort((a, b) => a.start_time.localeCompare(b.start_time));
    setAvailableSlots(mergedSlots);
  };

  const buildDatesBaseRequest = (
    department: ProviderDepartment,
    startDate: string,
    endDate: string,
    minimumAppointmentDateTime: moment.Moment,
  ) => {
    return {
      practiceId: data.address?.context_id,
      departmentIds: resolveDepartmentIds(department),
      providerId: Number.parseInt(providerDetails.providerId, 10),
      startDate,
      endDate,
      user_uuid: data?.userUuid || "",
      user_timezone: data.userTimezone?.standardName || "",
      minimum_day_time: minimumAppointmentDateTime.format("YYYY-MM-DDTHH:mm:ssZ"),
      duration: schedulingMetadata.durationMinutes,
      clinicianType: providerDetails.clinicianType,
    };
  };

  const fetchCombinedDates = async (baseRequest: ReturnType<typeof buildDatesBaseRequest>) => {
    const [virtualResponse, inPersonResponse] = await Promise.all([
      getProviderAvailableDates({ ...baseRequest, careFormat: CARE_FORMAT.TELEHEALTH }),
      getProviderAvailableDates({ ...baseRequest, careFormat: CARE_FORMAT.IN_PERSON }),
    ]);

    setCombinedAvailabilityDates([virtualResponse, inPersonResponse]);
  };

  const fetchSingleDates = async (
    baseRequest: ReturnType<typeof buildDatesBaseRequest>,
    department: ProviderDepartment,
  ) => {
    const careFormat = getCareFormatForDepartment(department);
    const response = await getProviderAvailableDates({
      ...baseRequest,
      careFormat,
    });

    if (response.success && response.data) {
      setAvailableDates(response.data.availableDates.map((dateStr) => dayjs(dateStr)));
      return;
    }

    const responseError =
      typeof response.error === "string" ? response.error : "Failed to load available dates";
    handleSingleAvailabilityError("dates", responseError, careFormat);
    handleAvailabilityFailure("dates", responseError, () => setAvailableDates([]));
  };

  const buildSlotsBaseRequest = (
    date: string,
    department: ProviderDepartment,
    minimumDayTime?: string,
  ) => {
    return {
      userUuid,
      practiceId: data.address?.context_id,
      providerId: Number.parseInt(providerDetails.providerId, 10),
      departmentIds: resolveDepartmentIds(department),
      startDate: date,
      endDate: date,
      userLatitude: data.address?.position?.lat || 0,
      userLongitude: data.address?.position?.lon || 0,
      minimum_day_time: minimumDayTime,
      duration: schedulingMetadata.durationMinutes,
      clinicianType: providerDetails.clinicianType,
    };
  };

  const fetchCombinedSlots = async (baseRequest: ReturnType<typeof buildSlotsBaseRequest>) => {
    const [virtualResponse, inPersonResponse] = await Promise.all([
      getProviderAvailableSlots({ ...baseRequest, careFormat: CARE_FORMAT.TELEHEALTH }),
      getProviderAvailableSlots({ ...baseRequest, careFormat: CARE_FORMAT.IN_PERSON }),
    ]);

    setCombinedAvailabilitySlots([
      { response: virtualResponse, careFormat: CARE_FORMAT.TELEHEALTH },
      { response: inPersonResponse, careFormat: CARE_FORMAT.IN_PERSON },
    ]);
  };

  const fetchSingleSlots = async (
    baseRequest: ReturnType<typeof buildSlotsBaseRequest>,
    department: ProviderDepartment,
  ) => {
    const careFormat = getCareFormatForDepartment(department);
    const response = await getProviderAvailableSlots({
      ...baseRequest,
      careFormat,
    });

    if (response.success && response.data) {
      setAvailableSlots(response.data.slots.map((slot) => ({ ...slot, careFormat })));
      return;
    }

    const responseError =
      typeof response.error === "string" ? response.error : "Failed to load available time slots";
    handleSingleAvailabilityError("slots", responseError, careFormat);
    handleAvailabilityFailure("slots", responseError, () => setAvailableSlots([]));
  };

  /**
   * Fetch available dates for selected department
   */
  const fetchAvailableDates = async (department: ProviderDepartment) => {
    if (!department) return;

    setIsLoadingDates(true);
    setShowError(false);

    try {
      const userTimezone = data.userTimezone?.standardName;
      const currentMoment = userTimezone ? moment().tz(userTimezone) : moment();
      const minimumAppointmentDateTime = getMinimumAppointmentMoment(
        new Date(),
        userTimezone,
        data.userRole,
      );
      const startOfCutoffDay = minimumAppointmentDateTime.clone().startOf("day");
      const startDate = startOfCutoffDay.format("MM/DD/YYYY");
      const endDateBase = isPrescriber
        ? currentMoment.clone().startOf("day")
        : startOfCutoffDay.clone();
      const endDate = endDateBase
        .add(schedulingMetadata.schedulingWindowDays, "days")
        .format("MM/DD/YYYY");

      logger.info({
        text: `Current datetime detected: ${currentMoment.format("MM/DD/YYYY hh:mm A")}, minimum appointment datetime: ${minimumAppointmentDateTime.format("MM/DD/YYYY hh:mm A")}, startDate for available dates: ${startDate}`,
        location: "ScheduleCard.fetchAvailableDates",
      });

      const baseRequest = buildDatesBaseRequest(
        department,
        startDate,
        endDate,
        minimumAppointmentDateTime,
      );

      // The selected Session Location fully determines the modality (Virtual -> telehealth,
      // any physical clinic -> in-person), so only that care format is requested.
      if (shouldFetchCombinedAvailability(department)) {
        await fetchCombinedDates(baseRequest);
      } else {
        await fetchSingleDates(baseRequest, department);
      }
    } catch (error) {
      const fallbackErrorMessage = "Failed to load available dates";
      const responseError =
        typeof error === "string"
          ? error
          : error instanceof Error && error.message
            ? error.message
            : fallbackErrorMessage;

      handleSingleAvailabilityError("dates", responseError, getCareFormatForDepartment(department));
      if (isTimeoutError(error) || isTimeoutError(responseError)) {
        navigate(APP_ROUTES.ERROR);
        return;
      }
      handleAvailabilityFailure("dates", responseError, () => setAvailableDates([]));
    } finally {
      setIsLoadingDates(false);
      setHasFetchedDates(true);
    }
  };

  /**
   * Fetch available time slots for selected date
   */
  const fetchAvailableSlots = async (date: string, department: ProviderDepartment) => {
    if (!date || !department) return;
    setIsLoadingSlots(true);
    setShowError(false);

    try {
      const userTimezone = data.userTimezone?.standardName;
      const minimumAppointmentDateTime = getMinimumAppointmentMoment(
        new Date(),
        userTimezone,
        data.userRole,
      );

      const parsedDate = moment(date, ["YYYY-MM-DD", "MM/DD/YYYY", moment.ISO_8601], true);
      const dateMoment = userTimezone ? parsedDate.tz(userTimezone, true) : parsedDate;
      const isMinimumDay = minimumAppointmentDateTime.isSame(dateMoment, "day");
      const minimum_day_time = isMinimumDay
        ? minimumAppointmentDateTime.format("YYYY-MM-DDTHH:mm:ssZ")
        : undefined;

      const baseRequest = buildSlotsBaseRequest(date, department, minimum_day_time);

      if (shouldFetchCombinedAvailability(department)) {
        await fetchCombinedSlots(baseRequest);
      } else {
        await fetchSingleSlots(baseRequest, department);
      }
    } catch (error) {
      const fallbackErrorMessage = "Failed to load available time slots";
      const responseError =
        typeof error === "string"
          ? error
          : error instanceof Error && error.message
            ? error.message
            : fallbackErrorMessage;

      handleSingleAvailabilityError("slots", responseError, getCareFormatForDepartment(department));
      if (isTimeoutError(error) || isTimeoutError(responseError)) {
        navigate(APP_ROUTES.ERROR);
        return;
      }
      handleAvailabilityFailure("slots", responseError, () => setAvailableSlots([]));
    } finally {
      setIsLoadingSlots(false);
    }
  };

  /**
   * Format time slot display (convert from HH:MM to HH:MM AM/PM)
   */
  const formatTimeSlot = (time: string): string => {
    const [hours, minutes] = time.split(":").map(Number);
    const period = hours >= 12 ? "PM" : "AM";
    const displayHours = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
    return `${displayHours}:${minutes.toString().padStart(2, "0")} ${period}`;
  };

  const getSlotIdentity = (slot: ProviderSlot): string => {
    const normalizedAppointmentIds = [...slot.appointment_ids].sort((a, b) => a - b).join("-");
    return `${slot.start_time}:${slot.department_id}:${slot.careFormat ?? "unknown"}:${normalizedAppointmentIds}`;
  };

  const formatDateWithOrdinal = (date: string): string => {
    const day = dayjs(date).date(); // Get the day of the month
    const suffix = getOrdinalSuffix(day);
    return `${dayjs(date).format(`dddd, MMMM D`)}${suffix}`;
  };

  /**
   * Validate if date is available
   */
  const validateDate = (date: string | null): boolean => {
    if (!date) return false;
    return availableDates.some((availableDate) => {
      return availableDate.isSame(date, "day");
    });
  };

  /**
   * Handle appointment booking
   */
  const onBookAppointment = async () => {
    if (!selectedTimeSlot || !selectedDepartment || !selectedDate) return;

    // Salesforce test leads may not freeze or schedule appointments.
    if (isTestRecordBlocked(data)) {
      setErrorMessage(TEST_RECORD_BLOCK_MESSAGE);
      setShowError(true);
      return;
    }

    await runExclusive(async () => {
      setIsFreezingAppointment(true);
      setShowError(false);

      try {
        const response = await freezeAppointmentSlots({
          userUuid,
          practiceId: data.address?.context_id,
          appointmentIds: selectedTimeSlot.appointment_ids,
          freezeAppointment: true,
        });

        if (response.success && response.data) {
          let selectedCareFormat: CareFormat;
          if (selectedTimeSlot.careFormat) {
            selectedCareFormat = selectedTimeSlot.careFormat;
          } else if (selectedDepartment.clinic_name === "Virtual") {
            selectedCareFormat = CARE_FORMAT.TELEHEALTH;
          } else {
            selectedCareFormat = CARE_FORMAT.IN_PERSON;
          }

          // Save appointment details using setData function
          setData("appointmentDetails", {
            providerId: providerDetails.providerId,
            apptDate: selectedDate,
            apptTime: formatTimeSlot(selectedTimeSlot.start_time),
            appointmentIds: selectedTimeSlot.appointment_ids,
            appointmentTypeId: selectedTimeSlot.appointment_type_id,
            careFormat: selectedCareFormat,
            apptLocation:
              providerDetails.departments.find(
                (dept) => dept.department_id === selectedTimeSlot.department_id,
              ) || null,
            userTimezone: getTimezoneAbbreviation(),
          });
          setAllowStartTimer(true);

          if (allowStartTimer) {
            // Start timer with server-provided timestamp for accurate synchronization
            if (response.data.frozenAt) {
              startTimerWithServerTimestamp(response.data.frozenAt);
            } else {
              // Fallback to standard startTimer if frozenAt not available
              logger.warn({
                level: "warn",
                text: "frozenAt timestamp not provided in freeze response, using client timer",
                location: "ScheduleCard.onBookAppointment",
              });
              startTimer();
            }
            setAllowStartTimer(false); // prevent timer from starting again after first call
          }
          // set /provider-profile route as completed
          markRouteAsComplete(APP_ROUTES.PROVIDER_PROFILE);
          // Navigate to patient information
          navigate(getInitialPatientInformationPath(data.paymentMethod));
        } else {
          setErrorMessage(
            typeof response.error === "string"
              ? response.error
              : "Failed to book appointment. Please try a different time slot.",
          );
          setShowError(true);
        }
      } catch {
        setErrorMessage("An unexpected error occurred. Please try again.");
        setShowError(true);
      } finally {
        setIsFreezingAppointment(false);
        // Save selected department, date, and time slot in context so they can be
        // restored if the user navigates away and back (e.g. browser Back button)
        setData("selectedDepartment", selectedDepartment);
        setData("selectedDate", selectedDate);
        setData("selectedTimeSlot", selectedTimeSlot);
      }
    }); // end runExclusive
  };

  // Effect: Set virtual department as default if sessionType is 'Virtual'
  useEffect(() => {
    // Selecting both virtual and in-person resolves to "no preference", so no modality is
    // auto-selected and the user chooses their session location explicitly.
    const sessionType = modalityPreference;
    if (!selectedDepartment) {
      const departmentOptions = generateDepartmentOptions(providerDetails);
      const virtualOption = departmentOptions.find((option) => option.value === "virtual");
      const inPersonOption = departmentOptions.filter((option) => option.value !== "virtual");

      // If sessionType is telehealth, only auto-select virtual if not already set
      if (sessionType === MODALITY.TELEHEALTH) {
        if (virtualOption) {
          setSelectedDepartment(virtualOption.department);
        }
      } else if (sessionType === MODALITY.IN_PERSON) {
        if (
          inPersonOption.length > 0 &&
          (data.isUnder12InPersonProceed || inPersonOption.length === 1)
        ) {
          setSelectedDepartment(inPersonOption[0].department);
        }
      } else if (departmentOptions.length === 1) {
        setSelectedDepartment(departmentOptions[0].department);
      }
    }
    // Do not include selectedDepartment in dependency array to avoid resetting after user selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.sessionType, providerDetails]);

  // Useeffect: to get dates for default selected department on component mount
  useEffect(() => {
    if (selectedDepartment && isUnfreezeComplete) {
      fetchAvailableDates(selectedDepartment);
    }
  }, [selectedDepartment, isUnfreezeComplete]);

  //Handle location change
  const handleLocationChange = (value: string) => {
    const option = generateDepartmentOptions(providerDetails).find((opt) => opt.value === value);
    const newDepartment = option?.department || null;

    if (!selectedDepartment || newDepartment?.department_id !== selectedDepartment?.department_id) {
      setAvailableDates([]); // Clear previous dates
      setSelectedDate(null); // Clear selected date
      setShowError(false);
      setSelectedTimeSlot(null);
      setSelectedDepartment(option?.department || null);
    }
  };

  // Effect: Load available slots when date changes
  useEffect(() => {
    if (
      selectedDate &&
      selectedDepartment &&
      availableDates.length > 0 &&
      validateDate(selectedDate)
    ) {
      setSelectedTimeSlot(null);
      fetchAvailableSlots(selectedDate, selectedDepartment);
    } else if (selectedDate && availableDates.length > 0) {
      // If the restored date is not valid anymore, clear it
      setSelectedDate(null);
      setAvailableSlots([]);
    } else {
      setHasFetchedDates(false);
      setAvailableSlots([]);
    }
  }, [selectedDate, selectedDepartment, availableDates]);

  // Effect: Restore the previously selected time slot (e.g. after navigating back)
  // once the freshly-fetched slots include it. Matching against the live fetch,
  // rather than trusting the persisted object, avoids re-selecting a slot that is
  // no longer actually available.
  useEffect(() => {
    if (selectedTimeSlot || !data.selectedTimeSlot) {
      return;
    }

    const persistedSlotIdentity = getSlotIdentity(data.selectedTimeSlot);
    const matchedSlot = availableSlots.find(
      (slot) => getSlotIdentity(slot) === persistedSlotIdentity,
    );

    // Stryker disable next-line ConditionalExpression: when no slot matches, matchedSlot
    // is undefined and setSelectedTimeSlot(undefined) is indistinguishable from leaving it
    // null, so the `if (true)` variant is an equivalent mutant.
    if (matchedSlot) {
      setSelectedTimeSlot(matchedSlot);
    }
    // getSlotIdentity is a pure function of its argument and is intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableSlots, selectedTimeSlot, data.selectedTimeSlot]);

  const shouldShowNoSlotsMsg =
    !isLoadingSlots && hasFetchedDates && selectedDate && availableSlots.length === 0 && !showError;

  let slotsContent: ReactNode = null;

  if (availableSlots.length > 0) {
    const selectedSlotIdentity = selectedTimeSlot ? getSlotIdentity(selectedTimeSlot) : null;
    const schedulingBlocked = isTestRecordBlocked(data);

    slotsContent = (
      <HourSlotsHolder isSmallestMobile={isSmallestMobile} data-testid="test-id-hour-slots-holder">
        {availableSlots.map((slot) => {
          const slotIdentity = getSlotIdentity(slot);
          const formattedTime = formatTimeSlot(slot.start_time);
          const isSelected = selectedSlotIdentity === slotIdentity;
          return (
            <HourSlot
              key={slotIdentity}
              data-testid={`test-id-schedule-card-hour-slot-${slotIdentity}`}
              variant="outline"
              isDisabled={schedulingBlocked}
              onClick={() => {
                if (schedulingBlocked) {
                  return;
                }
                setSelectedTimeSlot(slot);
                setShowError(false);
              }}
              css={{
                backgroundColor: isSelected ? "$app.color.slot.selected" : undefined,
                color: isSelected ? "white" : undefined,
                borderColor: isSelected ? "$app.color.slot.selected" : undefined,
                "&:hover": isSelected ? { backgroundColor: "$app.color.slot.selected" } : undefined,
              }}
            >
              {formattedTime}
            </HourSlot>
          );
        })}
      </HourSlotsHolder>
    );
  } else if (shouldShowNoSlotsMsg) {
    slotsContent = (
      <Text css={{ fontWeight: 500, color: "$app.color.text.status.error" }}>
        No available time slots for this date. Please select a different date.
      </Text>
    );
  }

  return (
    <Fragment>
      {/* Loading Overlay for appointment freezing */}
      <LoadingOverlay isLoading={isFreezingAppointment} />
      <Card
        css={{
          "abyss-card-root": {
            padding: isMobile ? "0" : "36px 24px",
            marginTop: isMobile ? "24px" : "0",
            height: isMobile ? "100%" : "fit-content",
            width: "100%",
            border: isMobile ? "none" : undefined, // Unset border for mobile
            boxShadow: isMobile ? "none" : undefined, // Unset boxShadow for mobile
          },
        }}
      >
        <CardContent data-testid="test-id-schedule-card-content">
          <CardHeaderContainer data-testid="test-id-schedule-card-header-container">
            <Heading
              css={{
                "abyss-heading-root": {
                  fontSize: "22.78px !important",
                  lineHeight: "28px !important",
                },
              }}
            >
              {"Schedule the first session"}
            </Heading>
            <Text css={{ fontSize: "16px", lineHeight: "24px" }}>
              {`All times are ${schedulingMetadata.sessionDurationMinutes} minute sessions, listed in ${getTimezoneAbbreviation()} timezone`}
            </Text>
          </CardHeaderContainer>

          <FormInfo data-testid="test-id-schedule-card-form-info-container" isMobile={isMobile}>
            <AppointmentDetailsInfo
              data-testid="test-id-schedule-card-appointment-details-info"
              isMobile={isMobile}
            >
              <SelectInput
                data-testid="test-id-schedule-card-session-location-select"
                css={
                  {
                    "abyss-select-input-container-wrapper": {
                      ".abyss-select-input-container": {
                        ".abyss-select-input-selection-container > div:first-of-type": {
                          whiteSpace: "unset", // Unset the white-space property
                        },
                      },
                    },
                  } as Partial<
                    Record<
                      | "abyss-select-input-container-wrapper"
                      | "abyss-select-input-root"
                      | "abyss-select-input-wrapper"
                      | "abyss-select-input-label"
                      | "abyss-select-input-menu-search-input",
                      CSSProperties
                    >
                  >
                }
                label="Session Location"
                value={
                  selectedDepartment?.clinic_name === "Virtual"
                    ? "virtual"
                    : selectedDepartment?.department_id?.toString() || ""
                }
                isRequired={true}
                options={generateDepartmentOptions(providerDetails).filter(
                  (opt) => !(data.isUnder12InPersonProceed && opt.value === "virtual"),
                )}
                onChange={(value) => handleLocationChange(value)}
                aria-label="Select a location"
                enableOutsideScroll={canvasIsInSalesforce}
              />
              <StyledDateInput
                data-testid="test-id-schedule-card-date-input"
                isRequired
                matchAnchorWidth
                enableOutsideScroll
                label="What day works best?"
                subText={isLoadingDates || !isUnfreezeComplete ? "Loading available dates..." : ""}
                onKeyDown={(event) => {
                  // Keep the date picker calendar-only while preserving common keyboard shortcuts.
                  if (event.ctrlKey || event.metaKey || event.altKey) {
                    return;
                  }
                  if (
                    event.key.length === 1 ||
                    event.key === "Backspace" ||
                    event.key === "Delete"
                  ) {
                    event.preventDefault();
                  }
                }}
                onPaste={(event) => {
                  event.preventDefault();
                }}
                excludeDate={(date) => {
                  return !availableDates.some((availableDate) => {
                    return availableDate.isSame(date, "day");
                  }); // Exclude dates not in available dates
                }}
                // `selectedDate` is always stored as MM/DD/YYYY (normalized on init and change).
                value={selectedDate ?? ""} // Empty string clears out selection on calendar if selectedDate is null
                onChange={(value) => {
                  // Validate the new date before updating state
                  const rawSelectedDate =
                    typeof value === "string" ? value : value?.target?.value || "";
                  if (validateDate(rawSelectedDate)) {
                    setSelectedDate(normalizeToApiDate(rawSelectedDate));
                    setShowError(false);
                  }
                }}
                isDisabled={!selectedDepartment || isLoadingDates || !isUnfreezeComplete}
              />

              {/* Time Slots */}
              {selectedDate ? (
                <RadioGroup
                  css={{
                    "abyss-radio-group-root": { width: "100%" },
                    "abyss-radio-group-container": { width: "100%" },
                    "abyss-radio-group-label": { fontWeight: 700 },
                  }}
                  label={`Available appointments for ${formatDateWithOrdinal(selectedDate)}`}
                  value={selectedTimeSlot?.start_time || ""}
                >
                  {isLoadingSlots ? (
                    <LoadingSlotsContainer>
                      <LoadingSpinner
                        size="lg"
                        ariaLoadingLabel="Loading time slots"
                        isLoading={isLoadingSlots}
                      />
                    </LoadingSlotsContainer>
                  ) : (
                    slotsContent
                  )}
                </RadioGroup>
              ) : (
                <Text css={{ fontWeight: 500 }}>
                  Select a day to see available appointment times
                </Text>
              )}
            </AppointmentDetailsInfo>

            <div data-testid="test-id-schedule-card-bottom-content-holder">
              <ErrorMessageHolder
                data-testid="test-id-schedule-card-error-message-holder"
                showError={showError}
              >
                <IconSymbol
                  icon="error"
                  variant="outlined"
                  color="$app.color.text.status.error"
                  size={20}
                  css={{ visibility: showError ? "visible" : "hidden" }}
                />
                <Text
                  css={{
                    color: "$app.color.text.status.error",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  {errorMessage}
                </Text>
              </ErrorMessageHolder>
              <ButtonHolder data-testid="test-id-schedule-card-button-holder">
                <Button
                  css={{ width: "100%" }}
                  size="lg"
                  id="obc-schedule-card-book-appt-btn"
                  data-testid="test-id-schedule-card-book-appointment-button"
                  data-obc-label={`Schedule Card Book Appointment Button for ${selectedDate} & ${selectedTimeSlot?.start_time}`}
                  onClick={onBookAppointment}
                  isDisabled={
                    !selectedTimeSlot ||
                    !selectedDepartment ||
                    isFreezingAppointment ||
                    isBookingPending ||
                    isUiBlocked ||
                    isTestRecordBlocked(data)
                  }
                >
                  Book Appointment
                </Button>
              </ButtonHolder>
            </div>
          </FormInfo>
        </CardContent>
      </Card>
    </Fragment>
  );
};
